// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Game Event Logger
 * 
 * Centralized system for logging all game events for Captain's Log and Messages.
 * Persists to localStorage and provides filtering/querying capabilities.
 */

import STORAGE_KEYS from './STORAGE_KEYS';

class GameEventLogger {
  constructor() {
    this.events = [];
    this.maxEvents = 500; // Keep last 500 events
    this.listeners = new Set();
    this.load();
  }

  /**
   * Event types
   */
  static EVENT_TYPES = {
    // Travel
    WARP_ENGAGED: 'warp_engaged',
    WARP_ARRIVED: 'warp_arrived',
    GATE_TRAVEL: 'gate_travel',
    ORBIT_ENTERED: 'orbit_entered',
    ORBIT_EXITED: 'orbit_exited',
    
    // Combat
    COMBAT_STARTED: 'combat_started',
    COMBAT_VICTORY: 'combat_victory',
    COMBAT_DAMAGE: 'combat_damage',
    ENEMY_DESTROYED: 'enemy_destroyed',
    
    // Trade
    TRADE_BUY: 'trade_buy',
    TRADE_SELL: 'trade_sell',
    
    // Scanning
    SCAN_STARTED: 'scan_started',
    SCAN_COMPLETE: 'scan_complete',
    ALIEN_DISCOVERED: 'alien_discovered',
    SYSTEM_DISCOVERED: 'system_discovered',
    
    // Communications
    MESSAGE_RECEIVED: 'message_received',
    DISTRESS_SIGNAL: 'distress_signal',
    STATION_HAIL: 'station_hail',
    
    // Ship
    REFUEL: 'refuel',
    REPAIR: 'repair',
    UPGRADE_PURCHASED: 'upgrade_purchased',
    MODULE_INSTALLED: 'module_installed',
    
    // Resources
    RESOURCE_LOW: 'resource_low',
    RESOURCE_CRITICAL: 'resource_critical',
    
    // Misc
    ACHIEVEMENT_UNLOCKED: 'achievement_unlocked',
    REPUTATION_CHANGED: 'reputation_changed'
  };

  /**
   * Priority levels
   */
  static PRIORITY = {
    LOW: 'low',
    NORMAL: 'normal',
    HIGH: 'high',
    CRITICAL: 'critical'
  };

  /**
   * Log an event
   */
  log(type, data = {}) {
    // Debug: log what we're receiving
    
    const event = {
      id: Date.now() + Math.random(),
      type,
      timestamp: new Date().toISOString(),
      time: this.formatTime(new Date()),
      priority: data.priority || this.getDefaultPriority(type),
      message: data.message || this.generateMessage(type, data),
      data: data,
      read: false
    };

    this.events.unshift(event); // Add to beginning
    
    // Trim to max events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }

    this.save();
    this.notifyListeners(event);
    
    return event;
  }

  /**
   * Get default priority for event type
   */
  getDefaultPriority(type) {
    const { EVENT_TYPES, PRIORITY } = GameEventLogger;
    
    switch (type) {
      case EVENT_TYPES.COMBAT_STARTED:
      case EVENT_TYPES.COMBAT_DAMAGE:
      case EVENT_TYPES.DISTRESS_SIGNAL:
      case EVENT_TYPES.RESOURCE_CRITICAL:
        return PRIORITY.CRITICAL;
        
      case EVENT_TYPES.COMBAT_VICTORY:
      case EVENT_TYPES.ENEMY_DESTROYED:
      case EVENT_TYPES.STATION_HAIL:
      case EVENT_TYPES.RESOURCE_LOW:
        return PRIORITY.HIGH;
        
      case EVENT_TYPES.TRADE_BUY:
      case EVENT_TYPES.TRADE_SELL:
      case EVENT_TYPES.SCAN_COMPLETE:
      case EVENT_TYPES.MESSAGE_RECEIVED:
        return PRIORITY.NORMAL;
        
      default:
        return PRIORITY.LOW;
    }
  }

  /**
   * Generate message from event type and data
   */
  generateMessage(type, data) {
    const { EVENT_TYPES } = GameEventLogger;
    
    switch (type) {
      case EVENT_TYPES.WARP_ENGAGED:
        return `Warp drive engaged. Destination: ${data.destination || 'Unknown'}`;
      case EVENT_TYPES.WARP_ARRIVED:
        return `Arrived at ${data.location || 'destination'}`;
      case EVENT_TYPES.GATE_TRAVEL:
        return `Warp gate activated. Traveling to ${data.universe || 'unknown universe'}`;
      case EVENT_TYPES.ORBIT_ENTERED:
        return `Entered orbit around ${data.location || 'planet'}`;
      case EVENT_TYPES.ORBIT_EXITED:
        return `Departed from ${data.location || 'orbit'}`;
        
      case EVENT_TYPES.COMBAT_STARTED:
        return `Combat encounter! ${data.enemyCount || 1} hostile${data.enemyCount > 1 ? 's' : ''} detected`;
      case EVENT_TYPES.COMBAT_VICTORY:
        return `Combat victory! All hostiles destroyed`;
      case EVENT_TYPES.COMBAT_DAMAGE:
        return `Hull damage: ${data.damage || 0} HP`;
      case EVENT_TYPES.ENEMY_DESTROYED:
        return `Enemy destroyed: ${data.enemyType || 'hostile'}`;
        
      case EVENT_TYPES.TRADE_BUY:
        return `Purchased ${data.quantity || 0}x ${data.item || 'goods'} for ${data.cost || 0} credits`;
      case EVENT_TYPES.TRADE_SELL:
        return `Sold ${data.quantity || 0}x ${data.item || 'goods'} for ${data.profit || 0} credits`;
        
      case EVENT_TYPES.SCAN_STARTED:
        return `Deep scan initiated on ${data.target || 'system'}`;
      case EVENT_TYPES.SCAN_COMPLETE:
        return `Scan complete. ${data.findings || 'Data collected'}`;
      case EVENT_TYPES.ALIEN_DISCOVERED:
        return `New alien species discovered: ${data.species || 'Unknown'}`;
      case EVENT_TYPES.SYSTEM_DISCOVERED:
        return `New system discovered: ${data.system || 'Unknown'}`;
        
      case EVENT_TYPES.MESSAGE_RECEIVED:
        return data.message || 'Incoming transmission';
      case EVENT_TYPES.DISTRESS_SIGNAL:
        return `Distress signal detected from ${data.source || 'unknown vessel'}`;
      case EVENT_TYPES.STATION_HAIL:
        return `Hailed by ${data.station || 'station'}: ${data.message || 'Docking clearance granted'}`;
        
      case EVENT_TYPES.REFUEL:
        return `Refueled: +${data.amount || 0}% antimatter`;
      case EVENT_TYPES.REPAIR:
        return `Repairs complete: ${data.system || 'hull'} restored`;
      case EVENT_TYPES.UPGRADE_PURCHASED:
        return `Upgrade installed: ${data.upgrade || 'Unknown'}`;
      case EVENT_TYPES.MODULE_INSTALLED:
        return `Module installed: ${data.module || 'Unknown'}`;
        
      case EVENT_TYPES.RESOURCE_LOW:
        return `Warning: ${data.resource || 'Resource'} running low`;
      case EVENT_TYPES.RESOURCE_CRITICAL:
        return `CRITICAL: ${data.resource || 'Resource'} critically low!`;
        
      case EVENT_TYPES.ACHIEVEMENT_UNLOCKED:
        return `Achievement unlocked: ${data.achievement || 'Unknown'}`;
      case EVENT_TYPES.REPUTATION_CHANGED:
        return `Reputation ${data.change > 0 ? 'increased' : 'decreased'} with ${data.species || 'aliens'}`;
        
      default:
        return data.message || 'Event logged';
    }
  }

  /**
   * Format time as HH:MM
   */
  formatTime(date) {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  }

  /**
   * Get all events
   */
  getAll() {
    return [...this.events];
  }

  /**
   * Get events by type
   */
  getByType(type) {
    return this.events.filter(e => e.type === type);
  }

  /**
   * Get events by priority
   */
  getByPriority(priority) {
    return this.events.filter(e => e.priority === priority);
  }

  /**
   * Get unread events
   */
  getUnread() {
    return this.events.filter(e => !e.read);
  }

  /**
   * Get recent events (last N)
   */
  getRecent(count = 10) {
    return this.events.slice(0, count);
  }

  /**
   * Mark event as read
   */
  markAsRead(eventId) {
    const event = this.events.find(e => e.id === eventId);
    if (event) {
      event.read = true;
      this.save();
    }
  }

  /**
   * Mark all as read
   */
  markAllAsRead() {
    this.events.forEach(e => e.read = true);
    this.save();
  }

  /**
   * Clear all events
   */
  clear() {
    this.events = [];
    this.save();
    this.notifyListeners({ type: 'clear' });
  }

  /**
   * Subscribe to events
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify listeners
   */
  notifyListeners(event) {
    this.listeners.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[GameEventLogger] Listener error:', error);
      }
    });
  }

  /**
   * Save to localStorage
   */
  save() {
    try {
      localStorage.setItem(STORAGE_KEYS.GAME_EVENT_LOG, JSON.stringify(this.events));
    } catch (error) {
      console.error('[GameEventLogger] Failed to save:', error);
    }
  }

  /**
   * Load from localStorage
   */
  load() {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.GAME_EVENT_LOG);
      if (saved) {
        this.events = JSON.parse(saved);
      }
    } catch (error) {
      console.error('[GameEventLogger] Failed to load:', error);
      this.events = [];
    }
  }

  /**
   * Get statistics
   */
  getStats() {
    const stats = {
      total: this.events.length,
      unread: this.getUnread().length,
      byType: {},
      byPriority: {}
    };

    this.events.forEach(event => {
      stats.byType[event.type] = (stats.byType[event.type] || 0) + 1;
      stats.byPriority[event.priority] = (stats.byPriority[event.priority] || 0) + 1;
    });

    return stats;
  }
}

// Create singleton instance
const gameEventLogger = new GameEventLogger();

export default gameEventLogger;
export { GameEventLogger };
